/**
 * Emotion styles for the shopping-lists dashboard: the mobile new-list slider's frame, the More
 * Tools menu, the filter drawer and the shared-list tooltip.
 *
 * Imported from outside the dashboard too. `ModalContainer`: `Shared/AddToShoppingList/`,
 * `Shared/DefineCutModal/DefineCutModalDesktop.tsx`, `Checkout/EditModal/EditModalDesktop.tsx`,
 * `AccountSettings/Helpers/UpdatePassword.tsx` and
 * `ShoppingCart/ShoppingCartBulkActionMenu/ClearCart/ClearCartDesktop.tsx`. `Divider`:
 * `Catalog/CatalogProductsDetails/CatalogProductsGuidedView/CatalogGuidedViewCart/`
 * `CatalogGuidedViewCartDesktop.tsx`. A change here restyles those.
 */
import { IndexedLayers } from "@ryerson/frontend.common";
import { Button } from "@ryerson/frontend.button";
import { Flex } from "@ryerson/frontend.layout";
import { Modal } from "@ryerson/frontend.modal";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ITheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";

/** `Modal` raised to the modal layer; used by the modals listed in the file header. */
export const ModalContainer = styled(Modal)`
	z-index: ${IndexedLayers.modal};
`;
/** Frame of the mobile new-list slider (`ShoppingListDashboard.tsx` → `getNewListSlideMenu`). */
export const ModalContentMobile = styled(Flex)`
	width: 100vw;
	overflow: auto;
	bottom: 0;

	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			background: ${backgroundColor};
		`;
	}};
`;

/** Full-width close-button bar of that slider; `backgroundColor` prop. */
export const CloseModalButtonOuterContainer = styled(Flex)`
	width: 100%;
	box-sizing: border-box;

	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			background: ${backgroundColor};
		`;
	}};
`;
/** The slider's close button, with white label text. */
export const CloseButton = styled(Button)`
	span {
		color: white;
	}
`;
/** 50 px square around the close button; `borderSpecs` is its left border. */
export const CloseModalButtonInnerContainer = styled(Flex)`
	width: 50px;
	height: 50px;
	box-sizing: border-box;

	${(props: any) => {
		const { borderSpecs } = props;
		return css`
			border-left: ${borderSpecs};
		`;
	}};
`;

/** More Tools popover body (`ShoppingList.Functions.tsx`); `props.background` is its colour. */
export const menuWrapper = (props: any) => css`
	background-color: ${props.background};
	border-color: ${props.background};
	padding: 15px 20px;
	width: max-content;
	box-shadow: rgba(0, 0, 0, 0.1) 4px -1px 20px 5px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
`;

/** Not imported anywhere; the table has no Shared With column. */
export const alignSharedWithCell = (props: any) => css`
	border-color: ${props.borderColor};
	padding-left: 10px;
	div > div > div:first-of-type {
		width: 100%;
		justify-content: flex-start;
	}
`;
/** Rotating chevron holder of the mobile More Tools trigger; the last `transition` (0.3 s) wins. */
export const PopoverIcon = styled.div`
	transition: transform 5s;
	transform: rotate(0deg);
	-o-transition: transform 0.3s;
	-ms-transition: transform 0.3s;
	-moz-transition: transform 0.3s;
	-webkit-transition: transform 0.3s;
	display: inline-block;
	margin-left: 10px;
	transition: transform 0.3s;
`;
/** One More Tools item; `color` is its hover colour. */
export const menuItemStyles = (color: any) => css`
	margin-bottom: 7px;
	:hover {
		color: ${color};
		& span:first-of-type {
			color: ${color};
		}
	}
`;
/** Filter drawer header row; `props.breakpoint` is where the mobile padding starts. */
export const shoppingListSearchStyle = (props: any) => css`
	width: 100%;
	padding: 0 10px 0 20px;
	@media only screen and (max-width: ${props.breakpoint}) {
		padding: 15px 10px 25px 20px;
	}
`;

/** Faint 1 px rule in the `color` prop. */
export const Divider = styled.hr`
	width: 100%;
	opacity: 0.1;
	height: 1px;
	margin-block-start: 0px;
	margin-block-end: 0px;
	${(props: any) => {
		const { color } = props;
		return css`
			color: ${color};
		`;
	}};
`;
/** Filter drawer background (`FiltersDrawer.tsx`). */
export const outerDrawerStyle = (theme: ITheme) => css`
	width: 100%;
	background-color: ${theme.colors.primary.darkerGray};
`;

/** Text of the shared-list tooltip (`ShoppingListTableComponents/TableUserTypeCell.tsx`). */
export const TooltipContent = styled(Typography)`
	margin: 10px 0px;
`;

/** Forces every icon inside to `iconColor`; wraps the shared-list tooltip icon. */
export const StyledFlex = styled(Flex)<{ iconColor: string }>`
	${(props) => {
		const { iconColor } = props;
		return css`
			svg {
				color: ${iconColor} !important;
			}
		`;
	}};
`;
